import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Copy,
  Inbox,
  RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import { syncApi } from "@/services/sync";
import type { Subscription, SubscriptionHealthLevel } from "@/types/api";

const healthStyle: Record<SubscriptionHealthLevel, { dot: string; label: string; badge: string }> = {
  ok: {
    dot: "bg-success",
    label: "Healthy",
    badge: "bg-success/10 text-success border-success/20",
  },
  warning: {
    dot: "bg-warning",
    label: "Attention",
    badge: "bg-warning/10 text-warning border-warning/20",
  },
  error: {
    dot: "bg-destructive",
    label: "Unhealthy",
    badge: "bg-destructive/10 text-destructive border-destructive/20",
  },
};

function formatMinutes(minutes: number | null): string {
  if (minutes === null) return "—";
  if (minutes < 0) {
    const absMin = Math.abs(minutes);
    if (absMin < 60) return `expired ${absMin}m ago`;
    return `expired ${Math.round(absMin / 60)}h ago`;
  }
  if (minutes < 60) return `in ${minutes}m`;
  if (minutes < 60 * 24) return `in ${Math.round(minutes / 60)}h`;
  return `in ${Math.round(minutes / (60 * 24))}d`;
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ts = new Date(iso);
  if (Number.isNaN(ts.getTime())) return "—";
  const diffSec = Math.round((Date.now() - ts.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  return `${Math.round(diffSec / 86400)}d ago`;
}

function truncate(value: string, head = 8, tail = 6): string {
  if (!value || value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

async function copyToClipboard(value: string, label: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied to clipboard`);
  } catch {
    toast.error(`Could not copy ${label}`);
  }
}

interface SubscriptionRowProps {
  subscription: Subscription;
}

const SubscriptionRow = ({ subscription }: SubscriptionRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const style = healthStyle[subscription.health];

  return (
    <>
      <TableRow
        className="cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
        data-testid="subscription-row"
      >
        <TableCell className="w-8">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <span className={cn("h-2.5 w-2.5 rounded-full", style.dot)} aria-hidden />
            <Badge variant="outline" className={style.badge}>
              {style.label}
            </Badge>
          </div>
        </TableCell>
        <TableCell className="font-mono text-xs">{truncate(subscription.drive_id, 10, 4)}</TableCell>
        <TableCell>
          <Badge variant="outline" className="capitalize">
            {subscription.status || "unknown"}
          </Badge>
        </TableCell>
        <TableCell>{formatMinutes(subscription.minutes_to_expiry)}</TableCell>
        <TableCell>
          <span className="text-xs">
            {subscription.delta_link_status ?? "—"}
            {subscription.delta_link_saved_at && (
              <span className="text-muted-foreground ml-1">
                ({formatRelative(subscription.delta_link_saved_at)})
              </span>
            )}
          </span>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {formatRelative(subscription.last_webhook_received_at)}
        </TableCell>
        <TableCell className="text-xs">
          {subscription.lifecycle_event ? (
            <>
              <span className="font-medium">{subscription.lifecycle_event}</span>
              <span className="text-muted-foreground ml-1">
                {formatRelative(subscription.lifecycle_event_at)}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell colSpan={8} className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <DetailField label="Subscription ID" value={subscription.subscription_id} copyable />
              <DetailField label="Drive ID" value={subscription.drive_id} copyable />
              <DetailField label="Notification URL" value={subscription.notification_url} copyable />
              <DetailField
                label="Lifecycle URL"
                value={subscription.lifecycle_notification_url ?? "—"}
                copyable={!!subscription.lifecycle_notification_url}
              />
              <DetailField label="Expires" value={subscription.expiration_datetime || "—"} />
              <DetailField label="Delta status" value={subscription.delta_link_status ?? "—"} />
              <DetailField
                label="Delta saved"
                value={subscription.delta_link_saved_at ?? "—"}
              />
              <DetailField
                label="Consecutive failures"
                value={String(subscription.consecutive_failures)}
              />
              <DetailField label="Last failure" value={subscription.last_failure_at ?? "—"} />
              <DetailField
                label="Last webhook received"
                value={subscription.last_webhook_received_at ?? "—"}
              />
              <DetailField
                label="Last webhook synced"
                value={subscription.last_webhook_synced_at ?? "—"}
              />
              <DetailField
                label="Needs recreation"
                value={subscription.needs_recreation ? "yes" : "no"}
              />
              <DetailField label="Created" value={subscription.created_at ?? "—"} />
              <DetailField label="Updated" value={subscription.updated_at ?? "—"} />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

interface DetailFieldProps {
  label: string;
  value: string;
  copyable?: boolean;
}

const DetailField = ({ label, value, copyable }: DetailFieldProps) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-muted-foreground whitespace-nowrap">{label}</span>
    <div className="flex items-start gap-2 min-w-0">
      <span className="font-mono text-xs break-all text-right">{value}</span>
      {copyable && value && value !== "—" && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(value, label);
          }}
          aria-label={`Copy ${label}`}
        >
          <Copy className="h-3 w-3" />
        </Button>
      )}
    </div>
  </div>
);

const ProjectSubscriptions = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "projects", projectId, "subscriptions"],
    queryFn: () => syncApi.getSubscriptions(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const subscriptions = data?.subscriptions ?? [];
  const counts = subscriptions.reduce(
    (acc, s) => {
      acc[s.health] += 1;
      return acc;
    },
    { ok: 0, warning: 0, error: 0 } as Record<SubscriptionHealthLevel, number>,
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader title="Administration" />

      <main className="flex-1 px-6 py-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <Button
                variant="ghost"
                size="sm"
                className="mb-2 -ml-2 text-muted-foreground"
                onClick={() => navigate(`/admin/projects/${projectId}/edit`)}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to project
              </Button>
              <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground">
                SharePoint Subscriptions
              </h1>
              <p className="text-muted-foreground mt-1">
                Microsoft Graph webhook subscriptions registered for this project's SharePoint drives.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
                className="gap-2"
              >
                <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Summary */}
          {!isLoading && !error && subscriptions.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="text-muted-foreground">
                {subscriptions.length} subscription{subscriptions.length === 1 ? "" : "s"}
              </span>
              {counts.error > 0 && (
                <Badge variant="outline" className={healthStyle.error.badge}>
                  {counts.error} unhealthy
                </Badge>
              )}
              {counts.warning > 0 && (
                <Badge variant="outline" className={healthStyle.warning.badge}>
                  {counts.warning} attention
                </Badge>
              )}
              {counts.ok > 0 && (
                <Badge variant="outline" className={healthStyle.ok.badge}>
                  {counts.ok} healthy
                </Badge>
              )}
            </div>
          )}

          {/* Body */}
          {isLoading ? (
            <Card>
              <CardContent className="p-6 space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </CardContent>
            </Card>
          ) : error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Failed to load subscriptions</AlertTitle>
              <AlertDescription className="flex items-center justify-between">
                <span>{error instanceof Error ? error.message : "Unknown error"}</span>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : subscriptions.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Inbox className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No subscriptions</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  This project has no scenarios linked to SharePoint drives, or no Graph subscriptions
                  have been registered yet.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Health</TableHead>
                    <TableHead>Drive</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Delta</TableHead>
                    <TableHead>Last webhook</TableHead>
                    <TableHead>Last lifecycle event</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.map((sub) => (
                    <SubscriptionRow key={sub.subscription_id} subscription={sub} />
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </main>

      <footer className="w-full py-4 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Lotus Bakeries. Internal Testing Portal.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ProjectSubscriptions;
